import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Grid,
  Typography,
  Tooltip,
  Checkbox,
  Button,
  InputAdornment,
  TextField,
  Chip,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getPracticeArea } from "../../../redux/Apps/Lawyerapp/CaseSlice/CaseSlice";
import { backend_api } from "../../../static/server";
import { SearchRounded } from "@mui/icons-material";
import { pageScrollStyle } from "../../../styles/styles";
import TemplateSkeleton from "../../TemplateSkeleton";
import { useDialog } from "../../DialogContext";
import MatterForm from "./MatterForm";
import axios from "axios";

const DrawerTemplate = () => {
  const { showDialog, closeDialog } = useDialog();
  const dispatch = useDispatch();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { token } = useSelector((state) => state.user);
  const { allPracticeArea } = useSelector((state) => state.case);
  const [allCaseTemplate, setAllCaseTemplate] = useState([]);
  const [nextUrl, setNextUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Get next page number from URL
  const getNextPage = (nextUrl) => {
    if (!nextUrl) return 1;
    const url = new URL(nextUrl);
    return parseInt(url.searchParams.get("page")) || 1;
  };

  const fetchTemplates = useCallback(
    async (searchQuery = "", isLoadMore = false) => {
      if (loading) return;

      setLoading(true);

      try {
        const page = isLoadMore ? getNextPage(nextUrl) : 1;
        const searchParam = searchQuery
          ? `&search=${encodeURIComponent(searchQuery)}`
          : "";
        const url = `${backend_api}case/template-register/?page=${page}${searchParam}`;

        const res = await axios.get(url, {
          headers: { Authorization: `Token ${token}` },
        });

        const resultsData = res.data.results || [];

        if (isLoadMore) {
          // For load more, append new data avoiding duplicates
          setAllCaseTemplate((prev) => {
            const existingIds = prev.map((item) => item.id);
            const newItems = resultsData.filter(
              (item) => !existingIds.includes(item.id)
            );
            return [...prev, ...newItems];
          });
        } else {
          // For initial load or search, replace data
          setAllCaseTemplate(resultsData);
        }

        setNextUrl(res.data.next);
      } catch (err) {
        console.error("Failed to load templates", err);
        if (!isLoadMore) {
          setAllCaseTemplate([]);
        }
      } finally {
        setLoading(false);
        if (initialLoading) {
          setInitialLoading(false);
        }
      }
    },
    [nextUrl, token, loading, initialLoading]
  );

  // Initial fetch
  useEffect(() => {
    if (token) {
      fetchTemplates();
    }
  }, [token]);

  // Search functionality with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== "") {
        fetchTemplates(searchTerm, false);
      } else {
        fetchTemplates("", false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleScroll = useCallback(
    (event) => {
      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;

      // Check if we're near the bottom (within 50px)
      if (!loading && nextUrl && scrollHeight - scrollTop - clientHeight < 50) {
        fetchTemplates(searchTerm, true);
      }
    },
    [loading, nextUrl, searchTerm, fetchTemplates]
  );

  // Filter templates based on search term (for immediate UI feedback)
  const filteredTemplates = allCaseTemplate.filter((t) =>
    t?.template_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPracticeAreaName = (area) => {
    if (!area) return "-";
    if (typeof area === "object" && area.name) return area.name;
    const found = allPracticeArea?.find((p) => p.id === area);
    return found?.name || "-";
  };

  useEffect(() => {
    if (token) {
      dispatch(
        getPracticeArea({
          url: `${backend_api}case/case-type/?case_service_type=practice_area`,
          token: token,
        })
      );
    }
  }, [dispatch, token]);

  const handleTemplateSelection = (templateId) => {
    setSelectedTemplate(templateId);
  };

  const handleApply = () => {
    if (!selectedTemplate) return;

    const selectedTemplateData = allCaseTemplate?.find(
      (template) => template.id === selectedTemplate
    );

    closeDialog();

    if (selectedTemplateData) {
      showDialog(
        <MatterForm
          formData={selectedTemplateData}
          onClose={() => {}}
          onSubmitSuccess={() => {}}
        />,
        {
          dialogTitle: "Preview Form",
          fullScreen: true,
          width: "100%",
          formType: "matter",
        }
      );
      console.log("Selected template data:", selectedTemplateData);
    }
  };

  // Show initial loading skeleton
  if (initialLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <TemplateSkeleton />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxHeight: "80vh",
        overflow: "auto",
        position: "relative",
        ...pageScrollStyle,
      }}
      onScroll={handleScroll}
    >
      {/* Search Header */}
      <Box
        sx={{
          position: "sticky",
          top: "0rem",
          background: "#FFFFFF40",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          zIndex: "99",
          p: "0.5rem",
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search templates..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded fontSize="small" />
              </InputAdornment>
            ),
            sx: {
              fontSize: "0.875rem",
              borderRadius: "0.375rem",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#dadada",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#00B3B0",
              },
            },
          }}
        />
      </Box>

      {/* Templates List */}
      <Box sx={{ py: "0.5rem", px: "1rem" }}>
        {filteredTemplates && filteredTemplates.length > 0 ? (
          <Grid container spacing={1}>
            {filteredTemplates.map((item, i) => {
              const isSelected = selectedTemplate === item.id;
              return (
                <Grid key={`${item.id}-${i}`} item xs={12}>
                  <Box
                    sx={{
                      borderRadius: "0.375rem",
                      border: `1px solid ${isSelected ? "#00B3B0" : "#DADADA"}`,
                      transition: "all 0.2s ease",
                      cursor: "pointer",
                      position: "relative",
                      "&:hover": {
                        borderColor: "#00B3B0",
                      },
                      ...(isSelected && {
                        boxShadow: "0 0 0 1px #00B3B0",
                      }),
                    }}
                    onClick={() => handleTemplateSelection(item.id)}
                  >
                    <Box sx={{ p: "0.75rem" }}>
                      {/* Header */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Checkbox
                            checked={isSelected}
                            size="small"
                            sx={{
                              p: 0.5,
                              mr: 0.5,
                              "&.Mui-checked": {
                                color: "primary.main",
                              },
                            }}
                          />
                          <Tooltip title={item?.template_name || "-"}>
                            <Typography
                              sx={{
                                fontSize: "0.875rem",
                                fontWeight: isSelected ? 600 : 500,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                color: isSelected ? "primary.main" : "inherit",
                              }}
                            >
                              {item?.template_name || "-"}
                            </Typography>
                          </Tooltip>
                        </Box>
                        <Box>
                          {item?.is_standard && (
                            <Chip
                              label="Standard"
                              size="small"
                              color="primary"
                              sx={{
                                fontSize: "0.7rem",
                                height: "1.4rem",
                              }}
                            />
                          )}
                        </Box>
                      </Box>

                      {/* Description */}
                      <Typography
                        sx={{
                          color: isSelected ? "text.primary" : "#606060",
                          fontSize: "0.75rem",
                          lineHeight: 1.4,
                          mb: 1,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {item?.template_description || "-"}
                      </Typography>

                      {/* Practice Area */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mt: 1,
                          borderTop: "1px solid #eee",
                          pt: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            color: isSelected ? "primary.main" : "#606060",
                            fontSize: "0.7rem",
                            fontWeight: 500,
                          }}
                        >
                          Practice Area:
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            fontWeight: 500,
                            ml: 0.5,
                            color: isSelected ? "primary.main" : "inherit",
                          }}
                        >
                          {getPracticeAreaName(item?.practice_area)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "10rem",
              flexDirection: "column",
            }}
          >
            <Typography
              sx={{
                textAlign: "center",
                color: "primary.main",
                fontSize: "0.875rem",
              }}
            >
              {searchTerm
                ? "No templates found matching your search"
                : "No templates available"}
            </Typography>
          </Box>
        )}

        {/* Loading indicator for infinite scroll */}
        {loading && !initialLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <TemplateSkeleton />
          </Box>
        )}
      </Box>

      {/* Apply Button */}
      <Box
        sx={{
          position: "sticky",
          bottom: "0rem",
          background: "#FFFFFF40",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          zIndex: "99",
          p: "0.5rem",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleApply}
          disabled={!selectedTemplate}
          size="small"
        >
          Apply & Open Form
        </Button>
      </Box>
    </Box>
  );
};

export default DrawerTemplate;
